import cn from 'classnames';
import type { Socket } from 'socket.io-client';
import { useMutation } from '@apollo/client';
import { ChangeEvent, FormEvent, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TMaxPlayers } from '@api/types/blackjack';
import { BREAKPOINTS } from '@consts/ui';
import { CREATE_BLACKJACK_GAME } from '@graphql/Mutations';
import {
  getUserIdFromToken,
  toastErrorMessage,
  toastSuccessMessage,
} from '@utils/utils';
import { ReactComponent as PadLockIcon } from '@assets/svg/padlock.svg';
import { ReactComponent as CoinIcon } from '@assets/svg/coin.svg';
import { SocketContext } from '@context/socket';
import Labeler from '@components/labeler';
import Container from '@components/container';
import RadioButton from '../components/radio-button';
import BetInput from '@components/bet-input';
import Button from '@components/button';
import useReduxState from '@hooks/useReduxState';
import styles from './create.module.scss';

interface IGameFormState {
  maxPlayers: TMaxPlayers;
  cost: number;
  isPrivate: boolean;
  userId: string;
}

interface ICreateGameProps {
  className?: string;
}

const CreateGame = ({ className = '' }: ICreateGameProps): JSX.Element => {
  const socket: Socket = useContext(SocketContext);
  const navigate = useNavigate();
  const [{ userData: user, blackjackLobby, chat, ui }] = useReduxState(
    (state) => state
  );
  const { min_stake } = blackjackLobby;

  const gameFormInitialState: IGameFormState = {
    isPrivate: false,
    maxPlayers: 2,
    cost: min_stake!,
    userId: getUserIdFromToken(),
  };

  const [{ maxPlayers, cost, isPrivate, userId }, setGameForm] =
    useState<IGameFormState>(gameFormInitialState);

  const formatInput = (name: string, value: string, checked: boolean) => {
    switch (name) {
      case 'maxPlayers':
        return +value as TMaxPlayers;
      case 'cost':
        return Math.max(Number(value), 0);
      case 'isPrivate':
        return checked;
    }
  };

  const onChange = ({
    target: { name, value, checked },
  }: ChangeEvent<HTMLInputElement>) =>
    setGameForm((prevState) => ({
      ...prevState,
      [name]: formatInput(name, value, checked),
    }));

  const isNewGameValid = (): boolean => {
    (!userId || !user.id) &&
      toastErrorMessage('Connect a wallet to create a game');

    return Boolean(userId) && Boolean(user.id);
  };

  /* API CALLS */

  const [createGame] = useMutation(CREATE_BLACKJACK_GAME);

  const createNewGame = async () => {
    try {
      const result = await createGame({
        variables: {
          cost,
          userId,
          max_players: maxPlayers,
          is_private: false,
        },
      });

      socket.emit('New-Blackjack');
      toastSuccessMessage('Game Created Succesfully!');
      navigate(`/games/blackjack/game/${result.data.game.uid}`);
    } catch (err: any) {
      toastErrorMessage(err.message);
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    isNewGameValid() && createNewGame();
  };

  /* Components */

  const RadioButtonItems = [...new Array(8)].map(
    (_, idx) =>
      idx >= 2 && (
        <RadioButton
          name="maxPlayers"
          key={idx}
          content={idx.toString()}
          id={idx.toString()}
          onChange={onChange}
          checked={maxPlayers === idx}
          value={idx.toString()}
          className={styles.maxPlayers__radio}
        />
      )
  );

  /* ClassNames */

  const containerClasses = cn(styles.createGame, 'panel-container', className, {
    [styles.chatVisible]: !chat.collapsed,
  });
  const maxPlayersClasses = cn(styles.maxPlayers, 'upper-section');
  const formItemsClasses = cn(styles.formItems, 'lower-section');

  return (
    <Container className={containerClasses} radius="md">
      <h3 className={styles.title}>
        Create <br /> a Game
      </h3>
      <form className={styles.createGame__form} onSubmit={handleSubmit}>
        <section className={maxPlayersClasses}>
          <Labeler label="Players">
            <div className={styles.maxPlayers__group}>{RadioButtonItems}</div>
          </Labeler>
        </section>
        <section className={formItemsClasses}>
          <div className={styles.formItems__inputs}>
            <Labeler label="Cost">
              <BetInput
                name="cost"
                onChange={onChange}
                step="10"
                value={cost.toString()}
                icon={<CoinIcon />}
                reversed
                className={styles.formItems__input}
              />
            </Labeler>
            <Labeler
              label={ui.windowWidth < BREAKPOINTS.LG ? '' : 'Private game'}
            >
              <RadioButton
                name="isPrivate"
                id="isPrivate"
                content={<PadLockIcon />}
                onChange={onChange}
                checked={isPrivate}
                checkbox
                className={styles.formItems__private}
                iconVariant="fill"
                withBorder
              />
            </Labeler>
          </div>
          <Button
            className={styles.formItems__submit}
            type="submit"
            color="green"
            radius="pill"
          >
            Create game
          </Button>
        </section>
      </form>
    </Container>
  );
};

export default CreateGame;
